import React, { useContext } from 'react';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import { Box, Typography, useTheme, Paper } from '@mui/material';
import { useStepperContext } from '../context/StepperContext';
import { useLoginContext } from '../../../../contexts/LoginContext';
import { StepperComponentProps } from '../../NewAccountStepper/types';
import { stepperStyles } from '../../shared/styles';
import ProfileConfirmation from '../../shared/components/ProfileConfirmation/ProfileConfirmation';
import { StatefulButton } from './StatefulButton';

const ConfirmationScreen: React.FC<StepperComponentProps> = ({
  description,
  label,
}) => {
  const { handleSave } = useStepperContext();
  const theme = useTheme();
  const { user, selectedProfileIndex } = useLoginContext();
  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];
  const { originalProfile, isNewProfile } = useStepperContext();

  // Deep equality check
  const profilesAreEqual = isEqual(originalProfile, selectedProfile);

  if (profilesAreEqual) {
    return (
      <Box sx={stepperStyles.pageContainer}>
        <Box sx={stepperStyles.centeredContent}>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={stepperStyles.pageTitle(theme)}
          >
            No Modifications Found
          </Typography>
          <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
            No changes were detected in your profile. If you intended to make
            changes, please go back and modify your profile.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={stepperStyles.pageContainer}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={stepperStyles.pageTitle(theme)}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
          {description}
        </Typography>
        <Paper elevation={3}>
          <ProfileConfirmation profile={selectedProfile} />
        </Paper>
      </Box>

      {/* Instructions Section */}
      <Typography
        variant="body1"
        sx={{ ...stepperStyles.instructionText(theme), mb: 2 }}
      >
        Please review the information above. If everything looks correct, click
        the button below to update your profile.
      </Typography>

      {/* Action Button */}
      <Box sx={stepperStyles.centeredActionContainer}>
        <StatefulButton
          variant="contained"
          size="large"
          onClick={handleSave}
          sx={{
            ...stepperStyles.elevatedButton(theme),
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
          }}
        >
          {isNewProfile ? 'Save Profile' : 'Update Profile'}
        </StatefulButton>
      </Box>
    </Box>
  );
};

export default ConfirmationScreen;
